import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { UserCheck, UserPlus, Users } from 'lucide-react';
import userApi from 'api/requests/user';
import followApi from 'api/requests/follow';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import Text from 'components/_UI/Text';
import Loader from 'components/_UI/Loader';
import Footer from 'components/commons/Footer';
import { Column } from 'styled/alignment/Column';
import { FullyCentered } from 'styled/alignment/Center';
import { useAuth } from 'context/auth/Auth';
import LoginModal from 'modules/onboarding/sign-in/LoginModal';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// "2024-03-12 10:00:00" → "No Whizr desde mar 2024".
function sinceLabel(createdAt) {
    const date = createdAt ? new Date(String(createdAt).replace(' ', 'T')) : null;
    if (!date || Number.isNaN(date.getTime())) return '';

    return `No Whizr desde ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * Perfil público de uma pessoa (/u/{username}): o link que o app compartilha.
 * Quem tem o app cai direto nele (App Links / Universal Links); quem não tem
 * vê esta página e pode seguir pelo site.
 *
 * Amigo é seguimento mútuo, então o botão conta a história inteira: Seguir,
 * Seguir de volta, Seguindo, Amigos. Sem login, o Seguir abre o modal de
 * entrar e a página recarrega a relação quando a pessoa volta logada.
 */
export default function PublicUserPage() {
    const { username } = useParams();
    const { isAuth } = useAuth();

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [failed, setFailed] = useState(false);
    const [pending, setPending] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);
    const [confirmUnfollow, setConfirmUnfollow] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await userApi.publicProfile(username);
            setProfile(data);
            setNotFound(false);
            setFailed(false);
        } catch (error) {
            if (error?.response?.status === 404) setNotFound(true);
            else setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [username]);

    useEffect(() => {
        load();
    }, [load, isAuth]);

    const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || profile?.username || '';
    const firstName = profile?.first_name || fullName;

    useEffect(() => {
        if (fullName) document.title = `${fullName} (@${profile.username}) | Whizr`;
    }, [fullName, profile?.username]);

    function applyFollow(following) {
        setProfile(current => {
            const rel = current.relationship || {};
            const delta = following === !!rel.is_following ? 0 : (following ? 1 : -1);

            return {
                ...current,
                counts: { ...current.counts, followers: Math.max((current.counts?.followers || 0) + delta, 0) },
                relationship: { ...rel, is_following: following, is_friend: following && !!rel.follows_you },
            };
        });
    }

    async function setFollowing(following) {
        if (pending) return;

        setConfirmUnfollow(false);
        applyFollow(following);
        setPending(true);

        try {
            if (following) await followApi.follow(profile.id);
            else await followApi.unfollow(profile.id);
        } catch (error) {
            applyFollow(!following);
        } finally {
            setPending(false);
        }
    }

    function onFollowClick() {
        if (!isAuth) {
            setLoginOpen(true);
            return;
        }

        if (profile?.relationship?.is_following) {
            setConfirmUnfollow(true);
            return;
        }

        setFollowing(true);
    }

    if (loading) {
        return (
            <PageContainer>
                <FullyCentered style={{ minHeight: '60vh' }}>
                    <Loader />
                </FullyCentered>
            </PageContainer>
        );
    }

    if (notFound || failed || !profile) {
        return (
            <PageContainer>
                <FullyCentered style={{ minHeight: '60vh' }}>
                    <Column horizontalCenter>
                        <Text name='subtitle' color={colors.nightDark}>
                            {notFound ? 'Perfil não encontrado' : 'Não deu para abrir o perfil'}
                        </Text>
                        <Text color={colors.night}>
                            {notFound ? 'O link pode estar errado, ou a conta foi excluída.' : 'Confira a conexão e recarregue a página.'}
                        </Text>
                    </Column>
                </FullyCentered>
            </PageContainer>
        );
    }

    const relationship = profile.relationship;
    const since = sinceLabel(profile.created_at);

    const follow = relationship?.is_friend
        ? { label: 'Amigos', Icon: Users, active: true }
        : relationship?.is_following
            ? { label: 'Seguindo', Icon: UserCheck, active: true }
            : relationship?.follows_you
                ? { label: 'Seguir de volta', Icon: UserPlus, active: false }
                : { label: 'Seguir', Icon: UserPlus, active: false };

    const hint = !isAuth
        ? `Entre no Whizr para seguir ${firstName}.`
        : relationship?.is_me || relationship?.is_friend
            ? null
            : relationship?.is_following
                ? `Quando ${firstName} seguir você de volta, vocês viram amigos.`
                : 'Quem se segue nos dois sentidos vira amigo no Whizr.';

    return (
        <PageContainer>
            <ProfileHeader>
                {profile.background_photo
                    ? <Banner style={{ backgroundImage: `url(${profile.background_photo})` }} />
                    : <Banner />}

                <ProfileHeaderInner>
                    <AvatarWrapper>
                        <DynamicProfileImage img={profile.avatar} size={'136px'} word={profile.first_name} />
                    </AvatarWrapper>

                    <HeaderContent>
                        <HeaderTopRow>
                            <NameGroup>
                                <Name>{fullName}</Name>
                                <Handle>
                                    @{profile.username}
                                    {relationship?.follows_you && !relationship?.is_friend && <FollowsYou>Segue você</FollowsYou>}
                                </Handle>
                                {!!since && <Since>{since}</Since>}
                            </NameGroup>

                            {!relationship?.is_me && (
                                <FollowArea>
                                    {confirmUnfollow ? (
                                        <ConfirmRow>
                                            <ConfirmText>Deixar de seguir {firstName}?</ConfirmText>
                                            <GhostButton type='button' onClick={() => setConfirmUnfollow(false)}>Cancelar</GhostButton>
                                            <DangerButton type='button' onClick={() => setFollowing(false)}>Deixar de seguir</DangerButton>
                                        </ConfirmRow>
                                    ) : (
                                        <FollowButton type='button' onClick={onFollowClick} disabled={pending} $active={follow.active}>
                                            <follow.Icon size={16} />
                                            {follow.label}
                                        </FollowButton>
                                    )}
                                </FollowArea>
                            )}
                        </HeaderTopRow>

                        {!!profile.bio && <Bio>{profile.bio}</Bio>}

                        <Stats>
                            <Stat><b>{profile.counts?.followers ?? 0}</b> {profile.counts?.followers === 1 ? 'seguidor' : 'seguidores'}</Stat>
                            <Stat><b>{profile.counts?.following ?? 0}</b> seguindo</Stat>
                            <Stat><b>{profile.counts?.friends ?? 0}</b> {profile.counts?.friends === 1 ? 'amigo' : 'amigos'}</Stat>
                        </Stats>

                        {!!hint && <Hint>{hint}</Hint>}
                    </HeaderContent>
                </ProfileHeaderInner>
            </ProfileHeader>

            <AppSection>
                <AppInner>
                    <Text name='subtitle' color={colors.nightDark}>No app é melhor</Text>
                    <Text color={colors.night} marginTop='6px'>
                        Veja os rolês em que seus amigos vão, guarde ingressos e receba quando alguém te segue.
                    </Text>
                </AppInner>
            </AppSection>

            <Footer />

            <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
        </PageContainer>
    );
}

const PageContainer = styled.div`
    width: 100%;
    min-height: 100vh;
    background: ${colors.neutralBackground};

    @media screen and (min-width: ${breakpoints.lg}) {
        padding-top: 95px;
    }
`;

const ProfileHeader = styled.div`
    width: 100%;
    background: ${colors.neutralLight};
    border-bottom: 1px solid ${colors.neutralDark};
`;

const Banner = styled.div`
    width: 100%;
    height: 96px;
    background: linear-gradient(120deg, ${colors.occasionalPurple}, ${colors.primaryLight});
    background-size: cover;
    background-position: center;

    @media screen and (min-width: ${breakpoints.md}) {
        height: 160px;
    }
`;

const ProfileHeaderInner = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    position: relative;
    padding: 0 24px 28px;
`;

const AvatarWrapper = styled.div`
    position: absolute;
    top: -68px;
    left: 50%;
    transform: translateX(-50%);
    width: 136px;
    height: 136px;
    border-radius: 50%;
    overflow: hidden;
    border: 5px solid ${colors.neutralLight};
    background: ${colors.neutralLight};
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);

    & > picture > img {
        object-fit: cover;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        left: 24px;
        transform: none;
    }
`;

const HeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;
    padding-top: 84px;

    @media screen and (min-width: ${breakpoints.md}) {
        align-items: flex-start;
        text-align: left;
        padding-top: 16px;
        margin-left: 160px;
    }
`;

const HeaderTopRow = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: flex-end;
        justify-content: space-between;
    }
`;

const NameGroup = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;

    @media screen and (min-width: ${breakpoints.md}) {
        align-items: flex-start;
    }
`;

const Name = styled.h1`
    margin: 0;
    font-size: ${fonts.sizes.title};
    font-weight: ${fonts.weight.bold};
    color: ${colors.nightDark};

    @media screen and (min-width: ${breakpoints.md}) {
        font-size: 28px;
    }
`;

const Handle = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${fonts.sizes.default};
    color: ${colors.night};
`;

const FollowsYou = styled.span`
    padding: 2px 8px;
    border-radius: 999px;
    background: ${colors.occasionalPurple}14;
    color: ${colors.occasionalPurple};
    font-size: ${fonts.sizes.small};
    font-weight: ${fonts.weight.bold};
`;

const Since = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
`;

const Bio = styled.p`
    max-width: 640px;
    margin: 0;
    font-size: ${fonts.sizes.default};
    line-height: 1.5;
    color: ${colors.night};
`;

const Stats = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 18px;
`;

const Stat = styled.span`
    font-size: ${fonts.sizes.default};
    color: ${colors.night};

    b {
        color: ${colors.nightDark};
        font-variant-numeric: tabular-nums;
    }
`;

const Hint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;

const FollowArea = styled.div`
    flex-shrink: 0;
`;

const FollowButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    border-radius: 6px;
    font-size: ${fonts.sizes.default};
    font-weight: ${fonts.weight.bold};
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s ease;
    border: 2px solid ${({ $active }) => $active ? colors.neutralDark : colors.occasionalPurple};
    background: ${({ $active }) => $active ? colors.neutralLight : colors.occasionalPurple};
    color: ${({ $active }) => $active ? colors.night : colors.neutralLight};

    &:hover:not(:disabled) {
        border-color: ${colors.occasionalPurple};
        background: ${({ $active }) => $active ? colors.neutralDark : colors.secondaryPurple};
    }

    &:disabled {
        opacity: 0.7;
        cursor: wait;
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const ConfirmRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 8px;
`;

const ConfirmText = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: ${fonts.weight.bold};
    color: ${colors.nightDark};
`;

const GhostButton = styled.button`
    padding: 8px 14px;
    border-radius: 6px;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    color: ${colors.night};
    font-weight: ${fonts.weight.bold};
    cursor: pointer;
`;

const DangerButton = styled(GhostButton)`
    border-color: #DC2626;
    color: #DC2626;
`;

const AppSection = styled.div`
    width: 100%;
    background: ${colors.neutralBackground};
`;

const AppInner = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 32px 24px 48px;
    text-align: center;

    @media screen and (min-width: ${breakpoints.md}) {
        text-align: left;
        padding-left: 184px;
    }
`;
